import { profile } from "../data/profile";
import { leadershipOrg } from "../data/leadership";
import { experiences } from "../data/experience";

const employerName = Object.fromEntries(experiences.map((job) => [job.id, job.company]));

const affiliationOf = (project) => {
  const ids = project.companyIds ?? (project.companyId ? [project.companyId] : []);
  return ids.map((id) => employerName[id]).filter(Boolean).join(" · ") || "Independent";
};

export const detailFromProject = (project) => ({
  id: project.id,
  fileNo: `EXHIBIT #${project.evidenceNo}`,
  title: project.title,
  subtitle: project.role,
  summary: project.description,
  body:
    project.internal ?
      "Full exhibit dossier with workflow diagrams, mockups, and implementation notes — open the linked case file for the complete product story."
    : "Production deployment available for verification. Architecture emphasizes maintainable UI, clear data boundaries, and integrations that match how the business actually operates.",
  bullets: [
    project.withTeam ? "Cross-functional delivery with design and stakeholders." : "Solo ownership from concept through production.",
    project.internal ? "Internal exhibit route on this investigation board." : "Live URL attached for external audit.",
  ],
  meta: [
    { label: "Evidence no.", value: project.evidenceNo },
    { label: "Shipped at", value: affiliationOf(project) },
    { label: "Team", value: project.withTeam ? "Team" : "Solo" },
  ],
  stack: project.stack,
  links: project.link ?
    [{
      href: project.link,
      label: project.internal ? "Open exhibit" : "Visit live site",
      internal: Boolean(project.internal),
    }]
  : [],
  videos: project.videos ?? [],
  image: project.image,
  imageAlt: project.title,
});

export const detailFromExperience = (job) => ({
  id: job.id,
  fileNo: `EMPLOYMENT ${job.year}`,
  title: job.title,
  subtitle: job.company,
  summary: job.stack,
  body: "Verified employment record with scope spanning product delivery, stakeholder communication, and production support. Highlights reflect shipped outcomes rather than task lists.",
  bullets: job.bullets,
  meta: [
    { label: "Duration", value: job.duration },
    { label: "Status", value: job.current ? "Current role" : "Closed" },
    { label: "Stack", value: job.stack },
  ],
  links: job.link ? [{ href: job.link, label: "Company site", internal: false }] : [],
  image: job.logo,
  imageAlt: `${job.company} logo`,
  stamp: job.current ? "CURRENT POST" : null,
});

export const detailFromPriorRecord = (roles) => ({
  id: "prior-record",
  fileNo: "PRIOR RECORD 2017–2018",
  title: "Pre-engineering employment",
  subtitle: "Operations & manufacturing",
  summary: "Roles prior to full-time software engineering — documented for timeline completeness.",
  body: "These positions sit outside the primary engineering thread but inform work ethic, operations mindset, and on-the-ground process awareness.",
  bullets: roles.map((role) => `${role.title} · ${role.company} (${role.duration})`),
  meta: [{ label: "Period", value: "2017 — 2018" }],
  links: roles
    .filter((role) => role.link)
    .map((role) => ({ href: role.link, label: role.company, internal: false })),
});

export const detailFromEducation = (item) => ({
  id: item.id,
  fileNo: `RECORD ${item.evidenceNo}`,
  title: item.degree,
  subtitle: item.university,
  summary: `${item.duration} · ${item.status}`,
  body:
    item.primary ?
      "Primary degree on file. Coursework and capstone work aligned with information systems, web engineering, and practical software delivery."
    : "Supporting academic record — coursework documented for timeline verification.",
  bullets: [
    `Status: ${item.status}`,
    "Institution mark cross-checked against public registry.",
    item.primary ? "Highest credential in the investigation file." : "Supplemental coursework entry.",
  ],
  meta: [
    { label: "Duration", value: item.duration },
    { label: "Status", value: item.status },
  ],
  links: [{ href: item.link, label: "Verify institution", internal: false }],
  image: item.logo,
  imageAlt: item.university,
});

export const detailFromLeadershipOrg = () => ({
  id: leadershipOrg.id,
  fileNo: "VOLUNTEER OPS",
  title: leadershipOrg.title,
  subtitle: leadershipOrg.company,
  summary: leadershipOrg.duration,
  body: "Long-running volunteer leadership and operations service — parallel to professional engineering work. Scope includes people management, budgets, records, and crisis response.",
  bullets: [
    "Multi-role progression from secretary through regional program director.",
    "Budget stewardship and welfare resource allocation.",
    "Training, mentorship, and conflict resolution at community scale.",
  ],
  meta: [
    { label: "Duration", value: leadershipOrg.duration },
    { label: "Status", value: leadershipOrg.current ? "Active" : "Closed" },
  ],
  links: leadershipOrg.link ?
    [{ href: leadershipOrg.link, label: "Organization", internal: false }]
  : [],
  image: leadershipOrg.logo,
  imageAlt: leadershipOrg.company,
  stamp: leadershipOrg.current ? "ACTIVE" : null,
});

export const detailFromLeadershipRole = (role) => ({
  id: role.id,
  fileNo: "LEADERSHIP DOSSIER",
  title: role.title,
  subtitle: role.duration,
  summary: `Key skills: ${role.skills}`,
  body: "Volunteer executive operations — high-trust environment with financial, personnel, and pastoral-adjacent responsibilities.",
  bullets: role.bullets,
  meta: [
    { label: "Duration", value: role.duration },
    { label: "Skills", value: role.skills },
  ],
  stamp: role.current ? "ACTIVE" : null,
});

export const detailFromSkillGroup = (group) => ({
  id: group.id,
  fileNo: `SKILL CLUSTER ${group.index}`,
  title: group.label,
  subtitle: "Evidence network node group",
  summary: `${group.items.length} capabilities on record`,
  body: "Technologies and practices repeatedly referenced across exhibits and employment records — clustered for investigation board navigation.",
  bullets: group.items.map((item) => item.label),
  meta: [{ label: "Cluster", value: `${group.index} / 04` }],
});

export const detailFromFeaturedSkill = (skill) => ({
  id: skill.id,
  fileNo: "PRIMARY CAPABILITY",
  title: skill.label,
  subtitle: skill.badge,
  summary: skill.description,
  body: "Differentiator across CRM automation, AI-assisted workflows, and GoHighLevel systems — the thread connecting BBI, Built to Last, Grace, and custom growth stacks.",
  bullets: [
    "Lead response and qualification automation",
    "Pipeline design in GoHighLevel",
    "AI workflow integration without brittle one-offs",
  ],
  links: [{ href: skill.link, label: skill.linkLabel, internal: true }],
});

export const detailFromSubjectDossier = () => ({
  id: "subject-dossier",
  fileNo: profile.caseNumber,
  title: profile.legalName,
  subtitle: profile.title,
  summary: profile.summary,
  body: profile.brief,
  bullets: [
    `Also known as: ${profile.aliases.join(" · ")}`,
    profile.nowBuilding,
    `Location: ${profile.location}`,
  ],
  meta: [
    { label: "Subject ID", value: profile.subjectId },
    { label: "Experience", value: `${profile.experienceYears} years` },
    { label: "Status", value: profile.status },
  ],
  image: profile.photo,
  imageAlt: profile.legalName,
});

export const contactReport = {
  finding: "Finding: one software engineer, repeatedly caught shipping.",
  summary:
    "Open to software engineering roles — full-stack or frontend, with automation where it helps — and to product collaborations. The case stays open until someone makes contact.",
};

export const detailFromContactReport = () => ({
  id: "final-report",
  fileNo: "FINAL REPORT",
  title: contactReport.finding,
  subtitle: `${profile.displayName} · ${profile.title}`,
  summary: contactReport.summary,
  body: profile.brief,
  meta: [
    { label: "Email", value: profile.email },
    { label: "Location", value: profile.location },
  ],
  links: [
    { href: `mailto:${profile.email}`, label: "Send email", internal: false },
    ...profile.socials
      .filter((link) => link.id !== "email")
      .map((link) => ({ href: link.href, label: link.label, internal: false })),
  ],
  image: profile.sketch,
  imageAlt: `Sketch of ${profile.displayName}`,
  stamp: "CASE OPEN",
});
